import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Close as CloseIcon,
  PushPin as PinIcon,
  PushPinOutlined as PinOutlinedIcon,
  Streetview as StreetViewIcon,
} from '@mui/icons-material';
import type { MapRef } from 'react-map-gl';
import StreetViewThumb from './StreetViewThumb';
import { INTEL_COLORS, INTEL_MONO } from './IntelPopup';
import {
  CAMERA_CATEGORY_COLORS,
  CAMERA_OVERLAY_COLOR,
  cameraCategoryLabel,
  cameraDisplayLabel,
  type CameraFeatureCollection,
  type CameraLocationProperties,
} from '../../services/cameraOverlayService';
import { STREET_VIEW_THUMB_H, STREET_VIEW_THUMB_W, cameraHeading } from '../../services/cameraStreetViewService';

/** A tracked entity (COP device) near a camera. */
export type CameraNearbyEntity = {
  id: string;
  label: string;
  color: string;
  distanceM: number;
};

export type CameraInView = {
  camera: CameraLocationProperties;
  lon: number;
  lat: number;
  heading: number | null;
  /** Distance from the map centre (viewport mode) or from the nearest entity (controlled mode). */
  distanceM: number;
  /** Entities within range of this camera, nearest first (controlled mode). */
  entities?: CameraNearbyEntity[];
};

export type CamerasInViewStripProps = {
  /** Filtered camera features currently drawn on the map. */
  cameras: CameraFeatureCollection;
  mapRef: React.RefObject<MapRef | null>;
  /** Anything that changes when the viewport moves (e.g. `${lon},${lat},${zoom}`). */
  viewportKey: string;
  /**
   * Controlled mode: the caller decides which cameras to show (e.g. COP's
   * "cameras near live entities"), already sorted. Skips the viewport scan.
   */
  items?: CameraInView[];
  /** Header title; defaults to "Cameras in view". */
  title?: string;
  /** Header summary override (controlled mode). */
  summary?: string;
  /** Max thumbnails to request (admin `in_view_limit`). */
  limit: number;
  /** Whether inline previews are allowed (admin embed + key). Without it the strip lists cameras without images. */
  previews: boolean;
  onSelect: (item: CameraInView) => void;
  onClose: () => void;
  /** Distance from the bottom edge of the map container, px. */
  bottom?: number;
  /** Horizontal insets so the strip clears side panels/toolbars. */
  left?: number;
  right?: number;
  /** Camera id currently open in a popup (highlighted). */
  activeCameraId?: string | null;
  /** Render inline as a band inside a host card (e.g. the Pathfinder timeline)
   *  instead of floating over the map. Ignores bottom/left/right. */
  docked?: boolean;
  /**
   * Optional "callout" mode: pin tiles to float their Street View still on
   * the map next to the camera (briefing/screenshot layout). Keys come from
   * `calloutKey`. Omit to hide the pin controls (COP).
   */
  callouts?: {
    calloutKey: (item: CameraInView) => string;
    pinnedKeys: ReadonlySet<string>;
    visible: boolean;
    onTogglePin: (item: CameraInView) => void;
    onToggleVisible: () => void;
    onClear: () => void;
  };
  /**
   * Route order (Pathfinder): when the loaded track passed a camera, its first
   * pass time and sequence number, keyed by `key(item)`. Tiles are then laid
   * out in the order the track reached them (cameras the track never passed
   * follow, nearest first) and carry the sequence number.
   */
  sequence?: {
    key: (item: CameraInView) => string;
    rank: ReadonlyMap<string, { seq: number; time: number }>;
  };
};

const THUMB_W = STREET_VIEW_THUMB_W;
const THUMB_H = STREET_VIEW_THUMB_H;
const DEBOUNCE_MS = 250;

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

function fmtDistance(m: number): string {
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
}

/**
 * Bottom filmstrip of the cameras nearest the map centre that are currently in
 * view, each with a Street View still aimed along the camera's direction.
 * Click a tile to fly to the camera and open its popup.
 */
const CamerasInViewStrip: React.FC<CamerasInViewStripProps> = ({
  cameras,
  mapRef,
  viewportKey,
  items: controlledItems,
  title = 'Cameras in view',
  summary: summaryOverride,
  limit,
  previews,
  onSelect,
  onClose,
  bottom = 12,
  left = 12,
  right = 12,
  activeCameraId,
  docked = false,
  callouts,
  sequence,
}) => {
  const pinnedCount = callouts?.pinnedKeys.size ?? 0;
  const [snapshot, setSnapshot] = useState<{ items: CameraInView[]; inView: number } | null>(null);
  const controlled = controlledItems != null;
  const sequenceKey = sequence?.key;
  const sequenceRank = sequence?.rank;

  // Recompute what's in view, debounced while the user pans.
  useEffect(() => {
    if (controlled) return undefined;
    const t = window.setTimeout(() => {
      const map = mapRef.current?.getMap?.();
      if (!map) return;
      const bounds = map.getBounds();
      const center = map.getCenter();
      if (!bounds || !center) return;
      const west = bounds.getWest();
      const east = bounds.getEast();
      const south = bounds.getSouth();
      const north = bounds.getNorth();
      const inView: CameraInView[] = [];
      for (const f of cameras.features) {
        const coords = f.geometry?.coordinates;
        if (!Array.isArray(coords)) continue;
        const lon = Number(coords[0]);
        const lat = Number(coords[1]);
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
        if (lat < south || lat > north) continue;
        // Handle antimeridian-crossing bounds too.
        const inLon = west <= east ? lon >= west && lon <= east : lon >= west || lon <= east;
        if (!inLon) continue;
        inView.push({
          camera: f.properties,
          lon,
          lat,
          heading: cameraHeading(f.properties),
          distanceM: haversineM(center.lat, center.lng, lat, lon),
        });
      }
      // Route order first (the time the track reached each camera), then the
      // rest nearest-first. Without a sequence, nearest-first throughout.
      const timeOf = (c: CameraInView) => (sequenceKey && sequenceRank ? sequenceRank.get(sequenceKey(c))?.time : undefined);
      inView.sort((a, b) => {
        const ta = timeOf(a);
        const tb = timeOf(b);
        if (ta != null && tb != null) return ta - tb;
        if (ta != null) return -1;
        if (tb != null) return 1;
        return a.distanceM - b.distanceM;
      });
      setSnapshot({ items: inView.slice(0, Math.max(1, limit)), inView: inView.length });
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [cameras, mapRef, viewportKey, limit, controlled, sequenceKey, sequenceRank]);

  const items = controlled ? controlledItems.slice(0, Math.max(1, limit)) : (snapshot?.items ?? []);
  const total = controlled ? controlledItems.length : (snapshot?.inView ?? 0);
  const inRouteOrder = useMemo(
    () => Boolean(sequenceKey && sequenceRank && items.some((i) => sequenceRank.has(sequenceKey(i)))),
    [items, sequenceKey, sequenceRank]
  );

  const summary = useMemo(() => {
    if (summaryOverride) return summaryOverride;
    if (!controlled && !snapshot) return 'Scanning view…';
    if (total === 0) return controlled ? 'No cameras near entities' : 'No cameras in view';
    const order = inRouteOrder ? ' · route order' : '';
    if (total > items.length) return `${items.length} ${inRouteOrder ? 'of' : 'nearest of'} ${total.toLocaleString()} cameras${controlled ? '' : ' in view'}${order}`;
    return `${total} camera${total === 1 ? '' : 's'}${controlled ? '' : ' in view'}${order}`;
  }, [summaryOverride, controlled, snapshot, total, items.length, inRouteOrder]);

  return (
    <Box
      sx={
        docked
          ? { width: '100%' }
          : {
              position: 'absolute',
              left,
              right,
              bottom,
              zIndex: 3,
              display: 'flex',
              justifyContent: 'center',
              pointerEvents: 'none',
            }
      }
    >
      <Box
        sx={{
          pointerEvents: 'auto',
          maxWidth: '100%',
          bgcolor: INTEL_COLORS.bg,
          color: INTEL_COLORS.text,
          borderLeft: `3px solid ${CAMERA_OVERLAY_COLOR}`,
          px: 1.25,
          pt: 0.75,
          pb: 1,
          ...(docked
            ? {
                width: '100%',
                borderBottom: `1px solid ${INTEL_COLORS.border}`,
              }
            : {
                borderRadius: '10px',
                boxShadow: '0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.05)',
              }),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, pr: 0.5 }}>
          <StreetViewIcon sx={{ fontSize: 15, color: CAMERA_OVERLAY_COLOR }} />
          <Box
            sx={{
              fontSize: '0.64rem',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: INTEL_COLORS.textMuted,
              flex: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Box>
          <Box sx={{ fontSize: '0.68rem', color: INTEL_COLORS.textDim, whiteSpace: 'nowrap' }}>{summary}</Box>
          {callouts && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
              <Tooltip
                title={
                  pinnedCount === 0
                    ? 'Pin tiles below to float their Street View on the map next to the camera (for screenshots)'
                    : callouts.visible
                      ? 'Hide map callouts'
                      : 'Show map callouts'
                }
                placement="top"
              >
                <Box
                  role="button"
                  tabIndex={0}
                  aria-pressed={callouts.visible}
                  onClick={() => pinnedCount > 0 && callouts.onToggleVisible()}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && pinnedCount > 0) {
                      e.preventDefault();
                      callouts.onToggleVisible();
                    }
                  }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.75,
                    py: '1px',
                    borderRadius: '10px',
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    cursor: pinnedCount > 0 ? 'pointer' : 'default',
                    color: pinnedCount > 0 && callouts.visible ? INTEL_COLORS.bg : INTEL_COLORS.textDim,
                    bgcolor: pinnedCount > 0 && callouts.visible ? CAMERA_OVERLAY_COLOR : 'rgba(255,255,255,0.06)',
                    border: `1px solid ${pinnedCount > 0 ? CAMERA_OVERLAY_COLOR : INTEL_COLORS.border}`,
                    whiteSpace: 'nowrap',
                    '&:focus-visible': { outline: `2px solid ${INTEL_COLORS.blue}` },
                  }}
                >
                  <PinIcon sx={{ fontSize: 11 }} />
                  Callouts{pinnedCount > 0 ? ` ${pinnedCount}` : ''}
                </Box>
              </Tooltip>
              {pinnedCount > 0 && (
                <Tooltip title="Clear all callouts" placement="top">
                  <IconButton
                    size="small"
                    onClick={callouts.onClear}
                    aria-label="Clear all callouts"
                    sx={{ p: '2px', color: INTEL_COLORS.textDim, '&:hover': { color: INTEL_COLORS.text } }}
                  >
                    <CloseIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close cameras in view"
            sx={{ p: '2px', color: INTEL_COLORS.textDim, '&:hover': { color: INTEL_COLORS.text } }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {items.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              overflowX: 'auto',
              pb: 0.25,
              '&::-webkit-scrollbar': { height: 6 },
              '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 3 },
            }}
          >
            {items.map((item) => {
              const color =
                (item.camera.category && CAMERA_CATEGORY_COLORS[item.camera.category]) || CAMERA_OVERLAY_COLOR;
              const active = activeCameraId != null && activeCameraId === item.camera.camera_id;
              const pinned = callouts ? callouts.pinnedKeys.has(callouts.calloutKey(item)) : false;
              const distance = fmtDistance(item.distanceM);
              const entities = item.entities ?? [];
              const primary = entities[0];
              const label = cameraDisplayLabel(item.camera);
              const seq = sequenceKey && sequenceRank ? sequenceRank.get(sequenceKey(item)) : undefined;
              const tooltipHead = `${seq ? `#${seq.seq} · ` : ''}${label === item.camera.camera_id ? label : `${label} · ${item.camera.camera_id}`}`;
              const tooltip = primary
                ? `${tooltipHead} · ${cameraCategoryLabel(item.camera.category)} · ${entities
                    .map((e) => `${e.label} ${fmtDistance(e.distanceM)}`)
                    .join(', ')}`
                : `${tooltipHead} · ${cameraCategoryLabel(item.camera.category)} · ${distance} from centre`;
              return (
                <Tooltip
                  key={`${item.camera.camera_id}-${item.lon}-${item.lat}`}
                  title={tooltip}
                  placement="top"
                  enterDelay={300}
                >
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(item);
                      }
                    }}
                    sx={{
                      flex: '0 0 auto',
                      width: THUMB_W,
                      borderRadius: '6px',
                      outline: active ? `2px solid ${color}` : '2px solid transparent',
                      outlineOffset: 1,
                      cursor: 'pointer',
                      '&:hover': { outline: `2px solid ${active ? color : 'rgba(255,255,255,0.35)'}` },
                      '&:focus-visible': { outline: `2px solid ${INTEL_COLORS.blue}` },
                      '&:hover .civ-pin': { opacity: 1 },
                    }}
                  >
                    <Box sx={{ position: 'relative' }}>
                      {previews ? (
                        <StreetViewThumb
                          lat={item.lat}
                          lon={item.lon}
                          heading={item.heading}
                          enabled
                          width={THUMB_W}
                          height={THUMB_H}
                          onClick={() => onSelect(item)}
                          hideBadge
                        />
                      ) : (
                        <Box
                          sx={{
                            height: THUMB_H,
                            borderRadius: '6px',
                            bgcolor: INTEL_COLORS.bgRaised,
                            border: `1px solid ${INTEL_COLORS.border}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: INTEL_COLORS.textDim,
                          }}
                        >
                          <StreetViewIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                        </Box>
                      )}
                      {callouts && (
                        <Tooltip title={pinned ? 'Remove map callout' : 'Pin as map callout'} placement="top">
                          <IconButton
                            className="civ-pin"
                            size="small"
                            aria-label={pinned ? 'Remove map callout' : 'Pin as map callout'}
                            aria-pressed={pinned}
                            onClick={(e) => {
                              e.stopPropagation();
                              callouts.onTogglePin(item);
                            }}
                            onKeyDown={(e) => e.stopPropagation()}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              p: '3px',
                              opacity: pinned ? 1 : 0,
                              transition: 'opacity 120ms',
                              bgcolor: pinned ? CAMERA_OVERLAY_COLOR : 'rgba(0,0,0,0.6)',
                              color: pinned ? INTEL_COLORS.bg : INTEL_COLORS.text,
                              '&:hover': { bgcolor: pinned ? CAMERA_OVERLAY_COLOR : 'rgba(0,0,0,0.85)' },
                            }}
                          >
                            {pinned ? <PinIcon sx={{ fontSize: 13 }} /> : <PinOutlinedIcon sx={{ fontSize: 13 }} />}
                          </IconButton>
                        </Tooltip>
                      )}
                      {seq && (
                        <Box
                          aria-label={`Route sequence ${seq.seq}`}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            minWidth: 18,
                            height: 18,
                            px: 0.5,
                            borderRadius: 9,
                            bgcolor: color,
                            color: '#fff',
                            fontSize: '0.62rem',
                            fontWeight: 800,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 0 1.5px #fff, 0 0 0 2.5px rgba(0,0,0,0.5)',
                            pointerEvents: 'none',
                          }}
                        >
                          {seq.seq}
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, px: 0.25, minWidth: 0 }}>
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                      <Box
                        sx={{
                          fontFamily: label === item.camera.camera_id ? INTEL_MONO : undefined,
                          fontSize: '0.62rem',
                          fontWeight: label === item.camera.camera_id ? 400 : 600,
                          color: INTEL_COLORS.text,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                        }}
                      >
                        {label}
                      </Box>
                      <Box sx={{ fontSize: '0.58rem', color: INTEL_COLORS.textDim, whiteSpace: 'nowrap' }}>
                        {item.heading == null ? '' : `${Math.round(item.heading)}°`}
                      </Box>
                    </Box>
                    {primary && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.2, px: 0.25, minWidth: 0 }}>
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: primary.color,
                            border: '1px solid rgba(255,255,255,0.7)',
                            flexShrink: 0,
                          }}
                        />
                        <Box
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 600,
                            color: INTEL_COLORS.text,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {primary.label}
                        </Box>
                        <Box sx={{ fontFamily: INTEL_MONO, fontSize: '0.58rem', color: INTEL_COLORS.textDim, whiteSpace: 'nowrap' }}>
                          {fmtDistance(primary.distanceM)}
                          {entities.length > 1 ? ` +${entities.length - 1}` : ''}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CamerasInViewStrip;
